import { useCallback, useEffect, useMemo, useState } from 'react';
import { Title } from 'react-admin';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';

import {
    getPaymentCountries,
    getPaymentOverview,
    type PaymentCountryOption,
    type PaymentOverviewRecord,
    type PaymentOverviewStatus,
} from './paymentOverviewApi';
import { PremiumFunnelPanel } from './PremiumFunnelPanel';
import { PaymentLinksPanel } from './PaymentLinksPanel';

type PaymentTab = 'premium_funnel' | 'payment_links' | 'initiated' | PaymentOverviewStatus;

const tabs: Array<{ value: PaymentTab; label: string }> = [
    { value: 'premium_funnel', label: 'Premium funnel' },
    { value: 'payment_links', label: 'Airwallex links' },
    { value: 'initiated', label: 'Payment initiated overview' },
    { value: 'failed', label: 'Failed payments' },
    { value: 'succeeded', label: 'Successful payments' },
];

function toDateInput(date: Date) {
    return date.toISOString().slice(0, 10);
}

function addUtcDays(value: string, days: number) {
    const date = new Date(`${value}T00:00:00.000Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return toDateInput(date);
}

function formatDateTime(value: string) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
}

function formatMoney(amount: number, currency: string) {
    if (!currency) return amount.toLocaleString();
    try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch {
        return `${currency} ${amount.toLocaleString()}`;
    }
}

function statusColor(status: string): 'success' | 'error' | 'warning' | 'default' {
    if (status === 'succeeded') return 'success';
    if (status === 'failed') return 'error';
    if (status === 'pending' || status === 'awaiting_payment') return 'warning';
    return 'default';
}

function paymentStatusLabel(status: string) {
    return status ? status.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) : 'Unknown';
}

function PaymentRow({ payment }: { payment: PaymentOverviewRecord }) {
    const customerName = payment.userName || payment.userEmail || 'Unknown customer';
    return (
        <TableRow hover>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(payment.createdAtUtc)}</TableCell>
            <TableCell>
                <Typography variant="body2" fontWeight={700}>{customerName}</Typography>
                {payment.userEmail && payment.userEmail !== customerName ? (
                    <Typography variant="caption" color="text.secondary">{payment.userEmail}</Typography>
                ) : null}
            </TableCell>
            <TableCell>
                <Typography variant="body2" fontWeight={700}>{payment.planName || '—'}</Typography>
                <Typography variant="caption" color="text.secondary">{payment.purchaseType || '—'}</Typography>
            </TableCell>
            <TableCell>{payment.billingCountryCode || '—'}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                {formatMoney(payment.finalAmount, payment.billingCurrency)}
            </TableCell>
            <TableCell><Chip size="small" color={statusColor(payment.status)} label={paymentStatusLabel(payment.status)} /></TableCell>
            <TableCell sx={{ minWidth: 220 }}>
                <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {payment.paymentIntentId || '—'}
                </Typography>
                {payment.providerPaymentId ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        Provider: {payment.providerPaymentId}
                    </Typography>
                ) : null}
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(payment.updatedAtUtc)}</TableCell>
        </TableRow>
    );
}

export function PaymentOverviewPage() {
    const initialStartDate = useMemo(() => {
        const date = new Date();
        date.setUTCDate(date.getUTCDate() - 29);
        return toDateInput(date);
    }, []);
    const [tab, setTab] = useState<PaymentTab>('premium_funnel');
    const [startDate, setStartDate] = useState(initialStartDate);
    const [endDate, setEndDate] = useState(() => toDateInput(new Date()));
    const [countryCode, setCountryCode] = useState('');
    const [countries, setCountries] = useState<PaymentCountryOption[]>([]);
    const [records, setRecords] = useState<PaymentOverviewRecord[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        getPaymentCountries().then(setCountries).catch(() => setCountries([]));
    }, []);

    const load = useCallback(async () => {
        if (tab === 'premium_funnel' || tab === 'payment_links') {
            return;
        }
        if (!startDate || !endDate) {
            setError('Start date and end date are required.');
            return;
        }
        if (startDate > endDate) {
            setError('Start date cannot be after end date.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const result = await getPaymentOverview({
                startDate,
                endDate,
                countryCode: countryCode || undefined,
                status: tab === 'initiated' ? undefined : tab,
                page: page + 1,
                pageSize,
            });
            setRecords(result.items);
            setTotalCount(result.totalCount);
        } catch (reason) {
            setRecords([]);
            setTotalCount(0);
            setError(reason instanceof Error ? reason.message : 'Unable to load payment records.');
        } finally {
            setLoading(false);
        }
    }, [countryCode, endDate, page, pageSize, startDate, tab]);

    useEffect(() => {
        void load();
    }, [load]);

    const changeTab = (_event: React.SyntheticEvent, value: PaymentTab) => {
        setPage(0);
        setTab(value);
    };

    const todayUtc = toDateInput(new Date());
    const dayNavigationAnchor = endDate || todayUtc;
    const selectDay = (day: string) => {
        setStartDate(day);
        setEndDate(day);
        setPage(0);
    };

    return (
        <Box sx={{ p: { xs: 1.25, sm: 2, md: 3 }, bgcolor: '#f8fafc', minHeight: '100%', minWidth: 0 }}>
            <Title title="Payment Overview" />

            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ color: '#0f172a', fontWeight: 800, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                        Payment Overview
                    </Typography>
                    <Typography sx={{ mt: 0.5, color: '#64748b' }}>
                        Review every payment attempt and quickly separate failed and successful payments.
                    </Typography>
                </Box>
                {tab !== 'premium_funnel' && tab !== 'payment_links' ? <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                    onClick={() => void load()}
                    disabled={loading}
                    sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}
                >
                    Refresh
                </Button> : null}
            </Stack>

            <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
                <Tabs
                    value={tab}
                    onChange={changeTab}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{ px: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                    {tabs.map((item) => <Tab key={item.value} value={item.value} label={item.label} sx={{ textTransform: 'none', fontWeight: 700 }} />)}
                </Tabs>

                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} useFlexGap flexWrap="wrap" sx={{ p: 2 }}>
                    <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        useFlexGap
                        flexWrap="wrap"
                        aria-label="Payment day navigation"
                    >
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ArrowBackIosNewIcon />}
                            onClick={() => selectDay(addUtcDays(dayNavigationAnchor, -1))}
                        >
                            Previous
                        </Button>
                        <Button
                            size="small"
                            variant={startDate === todayUtc && endDate === todayUtc ? 'contained' : 'outlined'}
                            onClick={() => selectDay(todayUtc)}
                        >
                            Today
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            endIcon={<ArrowForwardIosIcon />}
                            onClick={() => selectDay(addUtcDays(dayNavigationAnchor, 1))}
                            disabled={dayNavigationAnchor >= todayUtc}
                        >
                            Next
                        </Button>
                    </Stack>
                    <TextField
                        label="Start date (UTC)"
                        type="date"
                        size="small"
                        value={startDate}
                        onChange={(event) => { setPage(0); setStartDate(event.target.value); }}
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: endDate || undefined } }}
                        sx={{ minWidth: { sm: 180 } }}
                    />
                    <TextField
                        label="End date (UTC)"
                        type="date"
                        size="small"
                        value={endDate}
                        onChange={(event) => { setPage(0); setEndDate(event.target.value); }}
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: startDate || undefined } }}
                        sx={{ minWidth: { sm: 180 } }}
                    />
                    {tab !== 'premium_funnel' && tab !== 'payment_links' ? <FormControl size="small" sx={{ minWidth: { sm: 230 } }}>
                        <InputLabel id="payment-country-filter-label">Country</InputLabel>
                        <Select
                            labelId="payment-country-filter-label"
                            label="Country"
                            value={countryCode}
                            onChange={(event) => { setPage(0); setCountryCode(String(event.target.value)); }}
                        >
                            <MenuItem value="">All countries</MenuItem>
                            {countries.map((country) => (
                                <MenuItem key={country.countryCode} value={country.countryCode}>
                                    {country.countryName ? `${country.countryName} (${country.countryCode})` : country.countryCode}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl> : null}
                </Stack>
            </Paper>

            {tab !== 'premium_funnel' && tab !== 'payment_links' && error ? <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={() => void load()}>Retry</Button>}>{error}</Alert> : null}

            {tab === 'premium_funnel' ? (
                <PremiumFunnelPanel startDate={startDate} endDate={endDate} />
            ) : tab === 'payment_links' ? (
                <PaymentLinksPanel />
            ) : <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer sx={{ maxWidth: '100%' }}>
                    <Table size="small" sx={{ minWidth: 1180 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                                <TableCell>Initiated</TableCell>
                                <TableCell>Customer</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell>Country</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Payment references</TableCell>
                                <TableCell>Last updated</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {records.map((payment) => <PaymentRow key={payment.paymentIntentId} payment={payment} />)}
                            {!loading && records.length === 0 ? (
                                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 7, color: 'text.secondary' }}>No payments match these filters.</TableCell></TableRow>
                            ) : null}
                            {loading ? (
                                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 7 }}><CircularProgress size={28} /></TableCell></TableRow>
                            ) : null}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    rowsPerPage={pageSize}
                    onPageChange={(_event, nextPage) => setPage(nextPage)}
                    onRowsPerPageChange={(event) => { setPage(0); setPageSize(Number(event.target.value)); }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </Paper>}
        </Box>
    );
}
